// ============================================
// FILE: /frontend/src/pages/SettingsPage.jsx
// PURPOSE: User settings and preferences
// ============================================

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Bell, Lock, Eye, Trash2, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../api/client';

export default function SettingsPage() {
  console.log('[SettingsPage] Component mounted');

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    inquiryAlerts: true,
    marketingEmails: false,
    profileVisibility: 'public'
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    console.log('[SettingsPage] Fetching user settings...');
    setFetchLoading(true);
    try {
      const response = await apiClient.get('/user/settings');
      console.log('[SettingsPage] Settings received:', response.data);
      setSettings(response.data.settings);
    } catch (error) {
      console.error('[SettingsPage] Fetch error:', error);
      toast.error('Failed to load settings');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSave = async () => {
    console.log('[SettingsPage] Saving settings:', settings);
    setLoading(true);
    
    try {
      const response = await apiClient.patch('/user/settings', settings);
      console.log('[SettingsPage] Save successful:', response.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('[SettingsPage] Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    console.log('[SettingsPage] Delete account requested');
    
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    if (!window.confirm('Final confirmation: All your data will be permanently deleted. Continue?')) {
      return;
    }

    console.log('[SettingsPage] Account deletion confirmed');
    
    try {
      await apiClient.delete('/user/account');
      toast.success('Account deleted successfully');
      window.location.href = '/';
    } catch (error) {
      console.error('[SettingsPage] Delete error:', error);
      toast.error('Failed to delete account');
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Settings</h1>

        {/* Notification Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">Receive email updates about your listings</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => {
                    console.log('[SettingsPage] Email notifications:', e.target.checked);
                    setSettings({ ...settings, emailNotifications: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-muted-foreground">Get text messages for important updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={(e) => {
                    console.log('[SettingsPage] SMS notifications:', e.target.checked);
                    setSettings({ ...settings, smsNotifications: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Inquiry Alerts</p>
                <p className="text-sm text-muted-foreground">Instant notifications for new inquiries</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.inquiryAlerts}
                  onChange={(e) => {
                    console.log('[SettingsPage] Inquiry alerts:', e.target.checked);
                    setSettings({ ...settings, inquiryAlerts: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Marketing Emails</p>
                <p className="text-sm text-muted-foreground">Tips, promotions, and newsletters</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.marketingEmails}
                  onChange={(e) => {
                    console.log('[SettingsPage] Marketing emails:', e.target.checked);
                    setSettings({ ...settings, marketingEmails: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Privacy Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <label className="block font-medium mb-2">Profile Visibility</label>
              <select
                value={settings.profileVisibility}
                onChange={(e) => {
                  console.log('[SettingsPage] Profile visibility:', e.target.value);
                  setSettings({ ...settings, profileVisibility: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="public">Public - Anyone can see</option>
                <option value="registered">Registered Users Only</option>
                <option value="private">Private - Hidden from search</option>
              </select>
              <p className="text-sm text-muted-foreground mt-2">
                Controls who can see your profile and contact information
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <button 
              onClick={() => {
                console.log('[SettingsPage] Change password requested');
                toast.info('Password change feature coming soon');
              }}
              className="w-full py-2 border rounded-md hover:bg-muted transition"
            >
              Change Password
            </button>
            <p className="text-sm text-muted-foreground mt-2 text-center">
              You'll receive an email with instructions
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Once you delete your account, there is no going back. All your listings, inquiries, and data will be permanently deleted.
              </p>
              <button 
                onClick={handleDeleteAccount}
                className="w-full py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
              >
                Delete Account
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}